/**
 * megaDex — the per-species Mega view's data contract (2026-09-03 owner
 * ask), the Dynamax view's twin.
 *
 * The species page's Mega view shows, per Mega FORM (Charizard X and Y are
 * two), the copies a Mega can be:
 *
 *   - DEFAULT (a Mega Evolution of this form, done at least once) is the
 *     existing Mega dex row: variant "mega", the base species key for the
 *     base Mega and the form's own id ("charizard-mega-y") for an extra
 *     form. Nothing new is invented for it: the Dex carousel's Mega tab,
 *     the scanner and this page read and write the same cell. It used to
 *     sit on page one as the "Mega" tile; it now lives here as Default.
 *   - SHINY / PERFECT / LUCKY / XXL / XXS are Mega-scoped copies of the full
 *     dex variants, stored as FORM rows under those variants using the
 *     reserved form id below for the base Mega (`shiny:6:mega`) and the
 *     extra form's own id for X/Y pairs (`shiny:6:charizard-mega-y`). Same
 *     trick as dynamaxDex: server sync round-trips formId untouched, grids
 *     never double-count form rows, and the form cascade makes a Shiny Mega
 *     catch light the base Shiny and Main cells (a shiny Mega Charizard IS
 *     a shiny Charizard).
 *
 * The cascade the form engine cannot know: any Mega-scoped copy implies the
 * Mega itself, so toggling a variant tile ON also ticks the DEFAULT row.
 * One-directional, like every cascade in the store.
 */
import { getDexEntryState, setManual, toggleManual } from "./dexStateStore";
import type { DexEntryState } from "./dexStateStore";
import { megaFormsForSpecies, speciesHasMega, type MegaFormRef } from "./megaLevels";
import { MEGA_EXTRA_FORMS } from "./megaGmaxSprites";
import type { PokedexVariant } from "./pogoPokedexVariantClassifier";

/**
 * Reserved form id for Mega-scoped dex rows of a species' BASE Mega. Not a
 * catalogue form on purpose (FORM_GROUPS knows costumes and real forms, and
 * consumers of unknown form ids fail closed), so it never leaks into the
 * forms grids while riding the form-row storage, sync and cascade paths.
 */
export const MEGA_FORM_ID = "mega";

/**
 * The Mega view's tile order: "mega" is the DEFAULT tile, the rest follow
 * the detail page's card order. XXL / XXS were dropped on 2026-09-07 (owner
 * ask): size is a property of the Pokémon, not of its Mega Evolution, so the
 * species' own XXL / XXS cells already hold it and a Mega-scoped copy only
 * asked the trainer to tick the same thing twice. The Dynamax view keeps its
 * size tiles (DYNAMAX_PAGE_VARIANTS); this change is the Mega toggle only.
 */
export const MEGA_PAGE_VARIANTS: readonly PokedexVariant[] = [
  "mega",
  "shiny",
  "perfect",
  "lucky",
];

/** The copies a Mega can be linked / tracked as: the view's tiles minus the Mega itself. */
export const MEGA_COPY_VARIANTS: readonly PokedexVariant[] = MEGA_PAGE_VARIANTS.filter((v) => v !== "mega");

/** The form id a Mega-scoped variant row is stored under for this Mega form. */
export function megaScopedFormId(form: Pick<MegaFormRef, "formId">): string {
  return form.formId ?? MEGA_FORM_ID;
}

/**
 * Whether a stored form id names a Mega form: the reserved base id or one of
 * the X/Y extra forms. Event links and gap tiles use this to tell a Mega
 * copy from a costume.
 */
export function isMegaFormId(formId: string | null | undefined): boolean {
  if (!formId) return false;
  return formId === MEGA_FORM_ID || MEGA_EXTRA_FORMS.some((f) => f.formId === formId);
}

/** The Mega form a (species, form id) pair names, or null when it is not a Mega id. */
export function megaFormForFormId(dexId: number, formId: string | null | undefined): MegaFormRef | null {
  if (!speciesHasMega(dexId)) return null;
  const forms = megaFormsForSpecies(dexId);
  if (!formId || formId === MEGA_FORM_ID) return forms[0] ?? null;
  return forms.find((f) => f.formId === formId) ?? null;
}

/** The stored row behind one Mega-view tile (null = never recorded). */
export function getMegaTileEntry(
  variant: PokedexVariant,
  form: MegaFormRef,
): DexEntryState | null {
  return variant === "mega"
    ? getDexEntryState("mega", form.dexId, form.formId)
    : getDexEntryState(variant, form.dexId, megaScopedFormId(form));
}

/**
 * Toggle one Mega-view tile, the Manual Edit tap. Returns true when the write
 * persisted. DEFAULT toggles the Mega dex row itself; a variant tile toggles
 * its Mega-scoped form row and, landing on "present", ensures the DEFAULT
 * row too: you cannot own a Shiny Mega without having Mega Evolved.
 */
export function toggleMegaTile(variant: PokedexVariant, form: MegaFormRef): boolean {
  if (variant === "mega") {
    return toggleManual("mega", form.dexId, form.formId) != null;
  }
  const next = toggleManual(variant, form.dexId, megaScopedFormId(form));
  if (next?.state === "present") {
    const megaPresent = getDexEntryState("mega", form.dexId, form.formId)?.state === "present";
    if (!megaPresent) setManual("mega", form.dexId, "present", form.formId);
  }
  return next != null;
}

/**
 * One-shot hand-off from the Mega dex grid (2026-09-15 owner report: a tap
 * on the Mega tab landed on page one with the Mega toggle off). Same
 * pattern as armDynamaxDetailView in dynamaxDex.ts: the grid ARMS this
 * right before it navigates, the species page CONSUMES it on mount, so it
 * fires once and a visit from anywhere else opens page one as normal.
 */
let pendingMegaView = false;

export function armMegaDetailView(): void {
  pendingMegaView = true;
}

/** Read-and-clear. False when nothing was armed. */
export function consumeMegaDetailView(): boolean {
  const v = pendingMegaView;
  pendingMegaView = false;
  return v;
}
